import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { useTheme } from '@/hooks/useTheme';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { User, Settings, Shield, Bell, Trash2, ArrowLeft, Mail, UserCircle, Lock, Download, HelpCircle, Activity, Clock, Calendar, FileText, Globe, Moon, Sun, Smartphone, Users, AlertTriangle, ExternalLink } from 'lucide-react';
import { SymptomHistory } from '@/components/tracking/SymptomHistory';
import { PasswordChangeDialog } from '@/components/PasswordChangeDialog';

export default function AccountSettings() {
  const { user, updateProfile, deleteAccount, signOut } = useAuth();
  const { preferences, updatePreferences, isLoading: preferencesLoading } = useUserPreferences();
  const { setDarkMode, isDark } = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [profile, setProfile] = useState({
    full_name: '',
    email: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [reminderTime, setReminderTime] = useState('09:00');

  useEffect(() => {
    if (user) {
      setProfile({
        full_name: user.user_metadata?.full_name || '',
        email: user.email || ''
      });
      fetchProfileData();
    }
  }, [user]);

  useEffect(() => {
    // Sync dark mode with theme hook when preferences change
    if (preferences?.dark_mode !== undefined && preferences.dark_mode !== isDark) {
      setDarkMode(preferences.dark_mode);
    }
    
    // Set reminder time from preferences  
    if (preferences?.daily_reminder_time) {
      setReminderTime(preferences.daily_reminder_time.slice(0, 5)); // Remove seconds
    }
  }, [preferences?.dark_mode, preferences?.daily_reminder_time, setDarkMode, isDark]);

  const fetchProfileData = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .single();
    
    if (data && !error) {
      setProfile(prev => ({
        ...prev,
        full_name: data.full_name || prev.full_name
      }));
    }
  };

  const handleProfileUpdate = async () => {
    setIsLoading(true);
    await updateProfile(profile);
    setIsLoading(false);
  };

  const handleDeleteAccount = async () => {
    const result = await deleteAccount();
    if (!result.error) {
      navigate('/');
    }
  };

  const exportUserData = async () => {
    if (!user) return;
    
    try {
      // Fetch user's assessment history
      const { data: assessments } = await supabase
        .from('assessment_histories')
        .select('*');
      
      const userData = {
        profile: profile,
        assessments: assessments || [],
        exportDate: new Date().toISOString()
      };
      
      const blob = new Blob([JSON.stringify(userData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `amiok-user-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Data exported",
        description: "Your personal data has been downloaded successfully."
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to export your data. Please try again.",
        variant: "destructive"
      });
    }
  };

  const generateHealthReport = async () => {
    if (!user) return;
    
    try {
      const { data: assessments } = await supabase
        .from('assessment_histories')
        .select('*')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false })
        .limit(10);

      const healthReport = {
        generatedAt: new Date().toISOString(),
        patientInfo: {
          name: profile.full_name,
          email: profile.email,
        },
        recentAssessments: assessments || [],
        healthSummary: "Based on recent assessments, this report provides an overview of health status.",
      };

      const blob = new Blob([JSON.stringify(healthReport, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `health-report-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Health Report Generated",
        description: "Your health summary report has been downloaded."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate health report. Please try again.",
        variant: "destructive"
      });
    }
  };

  const shareWithGP = () => {
    toast({
      title: "Feature Coming Soon",
      description: "Direct GP sharing will be available in a future update."
    });
  };

  const openHelpCenter = () => {
    window.open('https://help.amiok.app', '_blank');
  };

  const contactSupport = () => {
    const subject = encodeURIComponent('AmIOK Support Request');
    const body = encodeURIComponent('Hi AmIOK Support Team,\n\nI need assistance with:\n\n[Please describe your issue here]\n\nThank you!');
    window.open(`mailto:support@amiok.app?subject=${subject}&body=${body}`, '_blank');
  };

  const openPrivacyPolicy = () => {
    navigate('/privacy-policy');
  };

  const reportProblem = () => {
    const subject = encodeURIComponent('AmIOK Problem Report');
    const body = encodeURIComponent('Hi AmIOK Team,\n\nI encountered the following problem:\n\n[Please describe the issue in detail]\n\nDevice/Browser info:\n- Device: \n- Browser: \n- Time of issue: \n\nThank you!');
    window.open(`mailto:support@amiok.app?subject=${subject}&body=${body}`, '_blank');
  };

  const showMedicalDisclaimer = () => {
    toast({
      title: "Medical Disclaimer",
      description: "AmIOK is for informational purposes only and should not replace professional medical advice. Always consult healthcare providers for medical concerns."
    });
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="px-4 py-6 max-w-lg mx-auto">{/* Mobile-first container */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" onClick={() => navigate('/')} className="p-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-foreground">Account Settings</h1>
            <p className="text-sm text-muted-foreground">Manage your preferences</p>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Profile Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCircle className="h-5 w-5" />
                Profile Information
              </CardTitle>
              <CardDescription>
                Update your personal details and contact information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  value={profile.full_name}
                  onChange={(e) => setProfile(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="Enter your full name"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter your email"
                />
              </div>
              <Button onClick={handleProfileUpdate} disabled={isLoading}>
                {isLoading ? "Updating..." : "Update Profile"}
              </Button>
            </CardContent>
          </Card>

          {/* Privacy & Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Privacy & Notifications
              </CardTitle>
              <CardDescription>
                Control how you receive communications and manage your privacy preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="email-notifications">Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive important updates and health reminders</p>
                </div>
                <Switch
                  id="email-notifications"
                  checked={preferences.email_notifications}
                  onCheckedChange={(checked) => updatePreferences({ email_notifications: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="reminder-notifications">Health Check Reminders</Label>
                  <p className="text-sm text-muted-foreground">Get notified to complete regular health assessments</p>
                </div>
                <Switch
                  id="reminder-notifications"
                  checked={preferences.reminder_notifications}
                  onCheckedChange={(checked) => updatePreferences({ reminder_notifications: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="emergency-alerts">Emergency Alerts</Label>
                  <p className="text-sm text-muted-foreground">Receive urgent notifications for severe health risks</p>
                </div>
                <Switch
                  id="emergency-alerts"
                  checked={preferences.emergency_alerts}
                  onCheckedChange={(checked) => updatePreferences({ emergency_alerts: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="weekly-summary">Weekly Health Summary</Label>
                  <p className="text-sm text-muted-foreground">Get weekly insights about your health trends</p>
                </div>
                <Switch
                  id="weekly-summary"
                  checked={preferences.weekly_health_summary}
                  onCheckedChange={(checked) => updatePreferences({ weekly_health_summary: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="data-processing">Data Processing Consent</Label>
                  <p className="text-sm text-muted-foreground">Allow processing of health data for personalized insights</p>
                </div>
                <Switch
                  id="data-processing"
                  checked={preferences.data_processing_consent}
                  onCheckedChange={(checked) => updatePreferences({ data_processing_consent: checked })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security
              </CardTitle>
              <CardDescription>
                Manage your account security and authentication settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" onClick={() => setShowPasswordDialog(true)} className="w-full justify-start">
                <Lock className="h-4 w-4 mr-2" />
                Change Password
              </Button>
              <Button variant="outline" onClick={signOut} className="w-full justify-start">
                <Mail className="h-4 w-4 mr-2" />
                Sign Out of All Devices
              </Button>
            </CardContent>
          </Card>

          {/* Health Sharing & Collaboration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Health Sharing & Collaboration
              </CardTitle>
              <CardDescription>
                Manage how your health data can be shared with healthcare providers
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="share-healthcare">Share with Healthcare Providers</Label>
                  <p className="text-sm text-muted-foreground">Allow authorized healthcare providers to access your assessment data</p>
                </div>
                <Switch
                  id="share-healthcare"
                  checked={preferences.share_with_healthcare}
                  onCheckedChange={(checked) => updatePreferences({ share_with_healthcare: checked })}
                />
              </div>
              <Button variant="outline" onClick={generateHealthReport} className="w-full justify-start">
                <FileText className="h-4 w-4 mr-2" />
                Generate Health Summary Report
              </Button>
              <Button variant="outline" onClick={shareWithGP} className="w-full justify-start">
                <Calendar className="h-4 w-4 mr-2" />
                Share Assessment with GP
              </Button>
            </CardContent>
          </Card>

          {/* App Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                App Preferences
              </CardTitle>
              <CardDescription>
                Customize your app experience and interface settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="dark-mode">Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">Use dark theme for reduced eye strain</p>
                </div>
                <Switch
                  id="dark-mode"
                  checked={preferences.dark_mode}
                  onCheckedChange={(checked) => {
                    updatePreferences({ dark_mode: checked });
                    setDarkMode(checked);
                  }}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auto-backup">Automatic Data Backup</Label>
                  <p className="text-sm text-muted-foreground">Automatically backup your health data to the cloud</p>
                </div>
                <Switch
                  id="auto-backup"
                  checked={preferences.auto_backup}
                  onCheckedChange={(checked) => updatePreferences({ auto_backup: checked })}
                />
              </div>
              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="language">Language & Region</Label>
                  <Select value={preferences.language} onValueChange={(value) => updatePreferences({ language: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="de">Deutsch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="reminder-time">Daily Check-in Reminder Time</Label>
                  <Input
                    id="reminder-time"
                    type="time"
                    value={reminderTime}
                    onChange={(e) => {
                      setReminderTime(e.target.value);
                      updatePreferences({ daily_reminder_time: e.target.value + ':00' });
                    }}
                  />
                  <p className="text-sm text-muted-foreground">
                    Set a time for daily health assessment reminders
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Daily Symptom Tracking */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Daily Symptom Tracking
              </CardTitle>
              <CardDescription>
                View your symptom tracking history and patterns
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SymptomHistory />
            </CardContent>
          </Card>

          {/* Data Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                Data Management
              </CardTitle>
              <CardDescription>
                Download or manage your personal health data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" onClick={exportUserData} className="w-full justify-start">
                <Download className="h-4 w-4 mr-2" />
                Download My Data
              </Button>
              <p className="text-sm text-muted-foreground">
                Export all your personal data including assessment history and profile information in JSON format.
              </p>
            </CardContent>
          </Card>

          {/* Support */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                Support & Help
              </CardTitle>
              <CardDescription>
                Get assistance and learn more about using Am I OK?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" onClick={openHelpCenter} className="w-full justify-start">
                <HelpCircle className="h-4 w-4 mr-2" />
                Help Center & FAQs
              </Button>
              <Button variant="outline" onClick={contactSupport} className="w-full justify-start">
                <Mail className="h-4 w-4 mr-2" />
                Contact Support
              </Button>
              <Button variant="outline" onClick={openPrivacyPolicy} className="w-full justify-start">
                <FileText className="h-4 w-4 mr-2" />
                Privacy Policy & Terms
              </Button>
              <Button variant="outline" onClick={reportProblem} className="w-full justify-start">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Report a Problem
              </Button>
              <Button variant="outline" onClick={showMedicalDisclaimer} className="w-full justify-start">
                <Activity className="h-4 w-4 mr-2" />
                Medical Disclaimer
              </Button>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Danger Zone
              </CardTitle>
              <CardDescription>
                Irreversible actions that affect your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-4">
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => navigate('/delete-account-request')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Account Deletion Information
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Learn about account deletion options and submit requests
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Account Permanently
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription className="space-y-2">
                      <p>This action cannot be undone. This will permanently delete your account and remove all your data from our servers.</p>
                      <p className="font-medium">This includes:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Your profile information</li>
                        <li>All assessment history and results</li>
                        <li>Personalized health insights</li>
                        <li>Account preferences and settings</li>
                      </ul>
                      <p className="text-sm text-muted-foreground mt-4">
                        If you're experiencing issues, please contact our support team before deleting your account.
                      </p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAccount}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Yes, delete my account
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <p className="text-sm text-muted-foreground mt-4">
                Account deletion is permanent and cannot be undone. We recommend downloading your data first.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <PasswordChangeDialog 
        open={showPasswordDialog} 
        onOpenChange={setShowPasswordDialog} 
      />
    </div>
  );
}